import { createFileRoute, Link } from "@tanstack/react-router";
import { Code } from "../../components/Code";
import { AiPromptButton } from "../../components/AiPrompt";

export const Route = createFileRoute("/docs/quickstart")({
  component: Quickstart,
});

function Quickstart() {
  return (
    <>
      <h1>Quickstart</h1>
      <p className="lead">From nothing to a notification on your screen in under a minute.</p>

      <div className="ai-box">
        <div className="ai-copy">
          <h3>In a hurry? Let your AI do it</h3>
          <p>Copy the prompt and paste it into Cursor, Claude, or ChatGPT.</p>
        </div>
        <AiPromptButton />
      </div>

      <h2>1. Get your keys</h2>
      <p>
        No account required. The CLI mints a project with two keys: a <strong>publishable</strong> key
        for the browser and a <strong>secret</strong> key for your server.
      </p>
      <Code badge="terminal" copyText="npx notify-dev init">
{`$ npx notify-dev init

✓ notify project created
  Publishable key:  ntfy_pk_...   (safe in the browser)
  Secret key:       ntfy_sk_...   (server-side only)`}
      </Code>

      <h2>2. Add the script</h2>
      <p>
        Drop this into your page with your <strong>publishable</strong> key. It exposes a global{" "}
        <span className="icode">notify</span>.
      </p>
      <Code
        fname="index.html"
        badge="frontend"
        copyText={'<script src="https://api.getnotify.dev/notify.js?token=YOUR_PUBLISHABLE_KEY"></script>'}
      >
{`<script src="https://api.getnotify.dev/notify.js?token=YOUR_PUBLISHABLE_KEY"></script>`}
      </Code>

      <h2>3. Host the service worker</h2>
      <p>
        Browsers require the service worker to be same-origin with your page, so add a one-line file
        at your site root — <span className="icode">/notify-sw.js</span>:
      </p>
      <Code fname="notify-sw.js" badge="frontend" copyText={'importScripts("https://api.getnotify.dev/sw.js");'}>
{`importScripts("https://api.getnotify.dev/sw.js");`}
      </Code>

      <h2>4. Subscribe a user</h2>
      <p>Call this after the user opts in. It asks permission and registers the device.</p>
      <Code fname="app.js" badge="frontend" copyText={"await notify.subscribe(currentUser.id)"}>
{`await notify.subscribe(currentUser.id)`}
      </Code>

      <h2>5. Send from your backend</h2>
      <p>
        One request, using your <strong>secret</strong> key. Keep it in a server-side environment
        variable — never ship it to the browser.
      </p>
      <Code
        fname="server.js"
        badge="backend"
        copyText={`await fetch("https://api.getnotify.dev/send", {\n  method: "POST",\n  headers: { "content-type": "application/json" },\n  body: JSON.stringify({\n    token: process.env.NOTIFY_SECRET_KEY,\n    userId: user.id,\n    title: "Acme",\n    body: "Your export is ready!",\n  }),\n})`}
      >
{`await fetch("https://api.getnotify.dev/send", {
  method: "POST",
  headers: { "content-type": "application/json" },
  body: JSON.stringify({
    token: process.env.NOTIFY_SECRET_KEY,
    userId: user.id,
    title: "Acme",
    body: "Your export is ready!",
  }),
})`}
      </Code>

      <div className="callout">
        That’s the whole loop. If the notification didn’t appear, check that notifications are allowed
        for the site and that <span className="icode">/notify-sw.js</span> is reachable at your root.
      </div>

      <div className="doc-foot">
        <Link to="/docs" className="ilink">
          ← Overview
        </Link>
        <Link to="/docs/how-it-works" className="ilink">
          How it works →
        </Link>
      </div>
    </>
  );
}
